import { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Banner, Button, LoadingScreen } from '@cowoo/shared/ui-web';
import { ApiProblem, qk } from '@cowoo/shared';
import type { paths } from '@cowoo/api-client';
import { api } from '../../lib/api';
import { Field, Page, fieldClass } from '../../ui';

/**
 * The sections of § 16 that exist in P1, and the namespace each one edits.
 *
 * `ziele`, `planwerte`, `kampagnen` and `berichte` are P2 and are not listed:
 * a section that cannot be saved is worse than one that is not there.
 */
const SECTIONS = [
  /**
   * § 16 draws "USt-IdNr." and "Steuernummer" among the legal details, and they
   * are `billing.*` — the namespace of the dunning section next door. A section
   * therefore borrows keys from elsewhere, and saving sends one PUT per
   * namespace, because `PUT /admin/settings/{namespace}` refuses a foreign key
   * by design and that refusal is worth keeping.
   */
  { slug: 'allgemein', namespace: 'site', also: ['billing.vat_id', 'billing.tax_number'] },
  { slug: 'mahnwesen', namespace: 'billing', also: [] },
  { slug: 'buchung', namespace: 'booking', also: [] },
  { slug: 'app', namespace: 'app', also: [] },
] as const;

type Section = (typeof SECTIONS)[number];

/**
 * The namespaces `/admin/settings/{namespace}` accepts, taken from the client
 * rather than restated — a namespace the server drops must stop compiling here.
 */
type Namespace = NonNullable<paths['/admin/settings/{namespace}']['get']['parameters']['path']>['namespace'];

/** One namespace of the settings store. */
async function loadNamespace(namespace: Namespace): Promise<Record<string, unknown>> {
  const { data, error } = await api.GET('/admin/settings/{namespace}', {
    params: { path: { namespace } },
  });
  if (error) throw error;
  return data as Record<string, unknown>;
}

/** `billing.vat_id` → `billing`. */
const namespaceOf = (key: string): Namespace => key.split('.')[0] as Namespace;

/**
 * `/einstellungen/:section` (10-web-admin § 16).
 *
 * Owner only, and every change is audited by the server — these are the values
 * that decide what members are charged and when a contract is terminated.
 *
 * The form is generated from the namespace the server answers rather than
 * hand-written per field: the settings are a typed key/value store, the server
 * validates each value against its own schema, and a hand-written form would
 * drift from that the first time a key is added. Scalars get an input, objects
 * get JSON — honest about what they are rather than pretending a nested shape
 * is a text box.
 */
export function Settings() {
  const { t, i18n } = useTranslation();
  const { section: slug } = useParams();
  const queryClient = useQueryClient();
  const section: Section = SECTIONS.find((entry) => entry.slug === slug) ?? SECTIONS[0];

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [saved, setSaved] = useState(false);

  const settings = useQuery({
    queryKey: qk.admin.area('settings', section.slug),
    queryFn: async () => {
      const all = { ...(await loadNamespace(section.namespace)) };

      // The borrowed keys, read from their own namespace and no more of it.
      for (const namespace of new Set(section.also.map(namespaceOf))) {
        const extra = await loadNamespace(namespace);
        for (const key of section.also) {
          if (key in extra) all[key] = extra[key];
        }
      }
      return all;
    },
  });

  /**
   * A new section replaces the draft; an unsaved edit does not survive leaving.
   *
   * It must **not** clear `saved`: a successful save refetches, which lands
   * here, and clearing it there meant the confirmation was wiped by the very
   * request that earned it. The banner is hidden by `dirty` instead, so it goes
   * the moment the next edit is typed.
   */
  useEffect(() => {
    if (!settings.data) return;
    setDraft(Object.fromEntries(leaves(settings.data).map(({ path, value }) => [path, String(value ?? '')])));
  }, [settings.data]);

  // Changing section is a different form, so an old confirmation does not apply.
  useEffect(() => setSaved(false), [section.slug]);

  const save = useMutation({
    mutationFn: async () => {
      const body = rebuild(settings.data ?? {}, draft);
      // One PUT per namespace, each carrying only its own keys.
      const byNamespace = new Map<Namespace, Record<string, unknown>>();
      for (const [key, value] of Object.entries(body)) {
        const namespace = namespaceOf(key);
        byNamespace.set(namespace, { ...(byNamespace.get(namespace) ?? {}), [key]: value });
      }
      for (const [namespace, part] of byNamespace) {
        const { error } = await api.PUT('/admin/settings/{namespace}', {
          params: { path: { namespace } },
          body: part,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      setProblem(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('settings') });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  if (settings.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const saved0 = Object.fromEntries(
    leaves(settings.data ?? {}).map(({ path, value }) => [path, String(value ?? '')]),
  );
  const dirty = Object.entries(draft).some(([path, text]) => saved0[path] !== text);

  return (
    <Page>
      <nav className="border-line rounded-card flex w-[250px] flex-none flex-col gap-0.5 border bg-white p-3.5">
        <p className="text-label text-muted px-3 pb-2 pt-1 uppercase">{t('admin.settings.title')}</p>
        {SECTIONS.map((entry) => (
          <NavLink
            key={entry.slug}
            to={`/einstellungen/${entry.slug}`}
            className={({ isActive }) =>
              `font-body rounded-buttonSm flex h-[38px] items-center px-3 text-sm font-semibold ${
                isActive ? 'bg-teal-tint text-ink' : 'text-muted hover:text-ink'
              }`
            }
          >
            {t(`admin.settings.section.${entry.slug}`)}
          </NavLink>
        ))}
      </nav>

      <section className="border-line rounded-card flex min-w-0 flex-1 flex-col gap-4 border bg-white px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-webH3 text-ink">{t(`admin.settings.section.${section.slug}`)}</h1>
            <p className="text-adminBody text-muted">{t(`admin.settings.hint.${section.slug}`)}</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={!dirty} onClick={() => void settings.refetch()}>
              {t('admin.settings.discard')}
            </Button>
            <Button size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
              {t('common.save')}
            </Button>
          </div>
        </div>

        {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
        {saved && !dirty ? <Banner tone="success">{t('admin.settings.saved')}</Banner> : null}

        {groupsOf(draft).map((group) => (
          <div key={group.name ?? '_'} className="flex flex-col gap-2">
            {group.name ? (
              <p className="text-adminBody text-ink font-semibold">{label(t, i18n, 'group', group.name)}</p>
            ) : null}
            <div className="grid grid-cols-2 gap-3.5">
              {group.paths.map((path) => (
                <Field key={path} label={label(t, i18n, 'field', leafOf(path))}>
                  <input
                    value={draft[path] ?? ''}
                    onChange={(event) => setDraft({ ...draft, [path]: event.target.value })}
                    className={fieldClass}
                  />
                </Field>
              ))}
            </div>
          </div>
        ))}
      </section>
    </Page>
  );
}

/**
 * The settings are a nested key/value store; the screen is a flat form.
 *
 * `site.legal_entity` is an object of eight fields, and rendering it as a JSON
 * textarea asked staff to edit punctuation to change a company name — the
 * artboard draws eight labelled boxes, which is also what anyone would expect.
 * So the tree is flattened to leaf paths for editing and rebuilt on save.
 */
interface Leaf {
  path: string;
  value: unknown;
}

/**
 * Top-level setting keys contain dots of their own (`site.legal_entity`), so a
 * path cannot be joined with one: splitting `site.legal_entity.company` back
 * gave `site` › `legal_entity` › `company` and wrote into an object that does
 * not exist. `::` is the separator because no settings key has ever had one.
 */
const SEP = '::';

function leaves(node: Record<string, unknown>, prefix = ''): Leaf[] {
  return Object.entries(node).flatMap(([key, value]) => {
    const path = prefix === '' ? key : `${prefix}${SEP}${key}`;
    // Fully recursive: `site.travel_minutes` nests a second level
    // (`mannheim.car`), and stopping at the first turned that object into the
    // string "[object Object]" and wrote it back as one.
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? leaves(value as Record<string, unknown>, path)
      : [{ path, value }];
  });
}

/** Puts the edited strings back into the shape the server sent, typed as it was. */
function rebuild(original: Record<string, unknown>, draft: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = structuredClone(original);
  for (const [path, text] of Object.entries(draft)) {
    const parts = path.split(SEP);
    let node = out;
    for (const part of parts.slice(0, -1)) node = node[part] as Record<string, unknown>;
    const key = parts[parts.length - 1]!;
    const was = node[key];
    // A null that is still blank stays null rather than becoming "".
    if (was === null && text === '') continue;
    node[key] =
      typeof was === 'number' || (was === null && text.trim() !== '' && !Number.isNaN(Number(text)))
        ? Number(text)
        : typeof was === 'boolean'
          ? text === 'true'
          : text;
  }
  return out;
}

/** Leaves of the same object, in the order the server sent them. */
function groupsOf(draft: Record<string, string>): { name: string | null; paths: string[] }[] {
  const groups = new Map<string | null, string[]>();
  for (const path of Object.keys(draft)) {
    const parts = path.split(SEP);
    // `site.support::email` → group `support`; `site.travel_minutes` → none.
    // Everything above the leaf names the group, so a second level reads
    // "travel_minutes · mannheim" rather than collapsing into its parent.
    const name = parts.length === 1 ? null : parts.slice(0, -1).join(' · ').split('.').slice(1).join('.');
    groups.set(name === '' ? null : name, [...(groups.get(name === '' ? null : name) ?? []), path]);
  }
  return [...groups].map(([name, paths]) => ({ name, paths }));
}

/** The label key: the sub-field's name, or the setting's own last segment. */
const leafOf = (path: string): string =>
  path.includes(SEP) ? path.split(SEP).pop()! : path.split('.').slice(1).join('.') || path;

/**
 * The German label for a settings key, or the key.
 *
 * A key the server grows before anyone translates it still has to render — the
 * same lesson the audit screen taught, where a missing key took the whole
 * screen down because i18n throws rather than falling back.
 */
function label(t: TFunction, i18n: { exists: (key: string) => boolean }, kind: string, key: string): string {
  const full = `admin.settings.${kind}.${key}`;
  return i18n.exists(full) ? t(full) : key;
}
